import { Group, IconButton } from '@chakra-ui/react'
import { Panel, useReactFlow } from '@xyflow/react'
import type { FC } from 'react'
import { memo } from 'react'
import { PiArrowsOut, PiMinus, PiPlus } from 'react-icons/pi'
import { useLocation, useNavigate } from 'react-router-dom'

import ButtonWrapper from './nav/buttonWrapper'

import { Tooltip } from '@app/components/ui/tooltip'
import useStoreCurrentUser from '@app/hooks/useStoreCurrentUser'
import AddCardMenu, { buttonStyles } from '@app/pages/strategies/components/map/addCardMenu'
import { useStrategyMapContext } from '@app/pages/strategies/strategyMapContext'
import { abilitiesFor } from '@app/shared/authorization/caslProvider'
import ICON_MAP from '@app/utils/iconMap'

const MapControls: FC<{ mapControlsOffset: number }> = ({ mapControlsOffset }) => {
  const rf = useReactFlow()

  return (
    <Panel position="top-left" style={{ marginTop: `${mapControlsOffset}px` }}>
      <ButtonWrapper>
        <Group gap={0} orientation="vertical">
          <Tooltip content="Zoom In" positioning={{ placement: 'right' }}>
            <IconButton {...buttonStyles} onClick={() => rf.zoomIn()} aria-label="Zoom In">
              <PiPlus />
            </IconButton>
          </Tooltip>
          <Tooltip content="Zoom Out" positioning={{ placement: 'right' }}>
            <IconButton {...buttonStyles} onClick={() => rf.zoomOut()} aria-label="Zoom Out">
              <PiMinus />
            </IconButton>
          </Tooltip>
          <Tooltip content="Fit View" positioning={{ placement: 'right' }}>
            <IconButton {...buttonStyles} onClick={() => rf.fitView({ padding: 0.2 })} aria-label="Reset Zoom">
              <PiArrowsOut />
            </IconButton>
          </Tooltip>
        </Group>
      </ButtonWrapper>
    </Panel>
  )
}

const AiToolsControls: FC<{ aiControlsOffset: number }> = ({ aiControlsOffset }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const checked = location.pathname.includes('/ai')

  const onChange = () => {
    if (checked) {
      navigate('../map')
    } else {
      navigate('ai')
    }
  }

  return (
    <Panel position="top-left" style={{ marginTop: `${aiControlsOffset}px` }}>
      <ButtonWrapper>
        <Group gap={0} orientation="vertical">
          <Tooltip content="AI Assistant" positioning={{ placement: 'right' }}>
            <IconButton
              {...buttonStyles}
              onClick={onChange}
              aria-label="AI Assistant"
              data-toolbar-button-id="ai"
              color={checked ? 'purple.500' : undefined}
            >
              <ICON_MAP.AiCollection />
            </IconButton>
          </Tooltip>
        </Group>
      </ButtonWrapper>
    </Panel>
  )
}

type Props = {
  strategyId: string
}

const MapMenu: FC<Props> = ({ strategyId }) => {
  const { user } = useStoreCurrentUser()
  const { canEdit } = useStrategyMapContext()

  const abilities = abilitiesFor(user)
  const canUpdateStrategy = abilities.can('update', 'strategy')

  const panelGap = 24
  const aiControlsHeight = 48
  const aiControlsOffset = panelGap
  const mainControlsHeight = 386
  const mainControlsOffset = aiControlsHeight + panelGap * 2

  const mapControlsOffset = canUpdateStrategy ? aiControlsHeight + mainControlsHeight + panelGap : panelGap

  if (!canEdit || !user) {
    return <MapControls mapControlsOffset={mapControlsOffset} />
  }

  return (
    <>
      {canUpdateStrategy && (
        <>
          <AiToolsControls aiControlsOffset={aiControlsOffset} />
          <Panel position="top-left" style={{ marginTop: `${mainControlsOffset}px` }}>
            <ButtonWrapper>
              <AddCardMenu strategyId={strategyId} />
            </ButtonWrapper>
          </Panel>
        </>
      )}
      <MapControls mapControlsOffset={mapControlsOffset} />
    </>
  )
}

MapMenu.displayName = 'MapMenu'

export default memo(MapMenu)
